import { RouterProps } from 'react-router';
import FileSaver, { FileSaverOptions } from 'file-saver';
import moment from 'moment';
import CryptoJS from 'crypto-js';
import { SystemMessageIdEnum, SystemOptionTypesEnum } from 'app/bff/enums/system';
import { InsuranceAmountAllocateTypesEnum } from 'app/bff/enums/insurance';
import { CountriesIdEnum } from 'app/bff/enums/country';
import { TargetAgeTypesEnum } from 'app/bff/enums/target';
import { MbGetinfoResp } from 'app/bff/models/mb/mbGetinfo';
import { CountryResp } from 'app/bff/models/info/infoGetnationlist';
import storageService from 'app/core/services/storageService';
import { StorageKeysEnum } from 'app/core/enums/storage';
import store from 'app/store';
import environments from 'environments';
import _ from 'lodash';
const unicodeSubstring = require('unicode-substring');

/**
 * @description 附加 Script resource
 * @param resourcePath Resource 檔案路徑
 */
const appendScriptResource = (resourcePath: string) => {
  // Remove duplicated scripts
  const appendedScripts = document.querySelectorAll('script[src="' + resourcePath + '"]');
  Array.prototype.forEach.call(appendedScripts, (node) => node.parentNode.removeChild(node));
  // Append new script
  const script = document.createElement('script');
  script.src = resourcePath;
  document.body.appendChild(script);
};

/**
 * @description 檢查是否有重複的 Script resource
 * @param resourcePath Resource 檔案路徑
 */
const checkDuplicateScript = (resourcePath: string) => {
  const scripts = document.getElementsByTagName('script');
  const scriptsSrc = Array.prototype.slice.call(scripts).map((script) => script.src);
  let resourceURL = `${window.location.origin}${resourcePath}`;
  if (environments.application.homepage) {
    resourceURL = resourcePath;
  }
  return scriptsSrc.includes(resourceURL);
};

/**
 * @description 檔案儲存處理器
 * @param data 檔案資訊
 * @param filename 檔案名稱
 * @param options 相關選項
 */
const fileSaver = (data: Blob | string, filename?: string, options?: FileSaverOptions) => {
  FileSaver.saveAs(data, filename, options);
};

/**
 * @description 生成 Session Id
 * @param regenerate 重新生成
 */
const generateSessionId = (regenerate: boolean = false) => {
  // 說明：配合中台 API 規範，API Request 帶上由前端生成的 Session Id。
  const originalSessionId = storageService.getItem(StorageKeysEnum.SessionId);
  const newSessionId = `OIS${moment().unix()}`;
  if (!originalSessionId || regenerate) storageService.setItem(StorageKeysEnum.SessionId, newSessionId);
};

/**
 * @description 處理視窗滾動至目標 Section
 * @param event event object
 * @param sectionId Section Id
 * @param delay 延遲時間
 */
const handleWindowScrollToTargetSection = (event: React.SyntheticEvent | null, sectionId: string, delay: number = 0) => {
  if (event) event.preventDefault();
  setTimeout(() => {
    const headerElem = document.getElementsByTagName('header')[0];
    const headerNavElem = headerElem.getElementsByTagName('nav')[0];
    const headerHeight = headerNavElem.offsetHeight;
    const targetSection = document.getElementById(sectionId);
    const targetSectionTitle = targetSection?.querySelector('.section-title');
    if (targetSectionTitle) {
      const targetSectionTitleMarginBottom = parseInt(window.getComputedStyle(targetSectionTitle).marginBottom);
      const offsetY = (window.scrollY + targetSectionTitle.getBoundingClientRect().top) - (headerHeight + targetSectionTitleMarginBottom);
      window.scrollTo({ top: offsetY });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, delay);
};

/**
 * @description 處理導頁
 * @param url URL
 * @param history Router history
 */
const handleRedirect = (url: string, history: RouterProps['history']) => {
  const isExternal = url.includes('http');
  !isExternal ? history.push(url) : window.open(url, '_blank');
};

/**
 * @description 西元日期轉民國日期
 * @param date 日期
 */
const convertToTWDate = (date: string) => {
  const twDate = +moment(date).format('YYYYMMDD') - 19110000;
  const pattern = /(\d{3}|\d{2}|\d{1})(\d{2})(\d{2})/;
  const formatedDate = String(twDate).replace(pattern, '$1/$2/$3');
  return formatedDate;
};

/**
 * @description 民國日期轉西元日期
 * @param date 日期
 */
const convertToADDate = (date: string) => {
  const adDate = +date.replace(/[^0-9]/ig, '') + 19110000;
  const pattern = /(\d{4})(\d{2})(\d{2})/;
  const formatedDate = String(adDate).replace(pattern, '$1-$2-$3');
  return formatedDate;
};

/**
 * @description 移動陣列目標項目
 * @param arr 陣列資料
 * @param fromIndex 原始位置 (index)
 * @param toIndex 移動到的位置 (index)
 */
const moveArrayTargetItem = (arr: any[], fromIndex: number, toIndex: number) => {
  const targetItem = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, targetItem);
  return arr;
};

/**
 * @description 處理「民國日期」資料顯示 (轉中文格式)
 * @param date 民國日期字串 (YYY/MM/DD)
 */
const displayTWDate = (date?: string) => {
  if (!date) return '';
  const dateSnippets = date.includes('/') ? date.split('/') : date.includes('-') ? date.split('-') : [];
  return dateSnippets.length > 0 ? `民國 ${dateSnippets[0]} 年 ${dateSnippets[1]} 月 ${dateSnippets[2]} 日` : '';
};

/**
 * @description 處理「民國日期」資料隱碼顯示 (轉中文格式)
 * @param date 民國日期字串 (YYY/MM/DD)
 */
const protectedTWDate = (date?: string) => {
  if (!date) return '';
  const dateSnippets = date.includes('/') ? date.split('/') : date.includes('-') ? date.split('-') : [];
  return dateSnippets.length > 0 ? `民國 ${dateSnippets[0]} 年 ** 月 ** 日` : '';
};

/**
 * @description 處理「完整地址」資料顯示 (轉中文格式)
 * @description 說明：將「縣市」ID、「區域」ID、「街/路名」ID 轉中文格式，並組合地址
 * @param cityId 縣市 ID
 * @param areaId 區域ID
 * @param addr01 街/路名稱
 * @param addr02 巷
 * @param addr03 弄
 * @param addr04 號
 * @param addr05 號之
 * @param addr06 樓
 * @param addr07 樓之
 */
const displayAddress = (cityId?: string, areaId?: string, addr01?: string, addr02?: string, addr03?: string, addr04?: string, addr05?: string, addr06?: string, addr07?: string) => {
  if (!cityId || !areaId) return '';
  const cityState = store.getState().common.postcodes;
  const targetCity = cityState.find((opt) => opt.cityId === cityId);
  const targetArea = targetCity?.area.find((opt) => opt.areaId === areaId);
  const targetCityName = targetCity?.cityName ?? '';
  const targetAreaName = targetArea?.areaName ?? '';
  return (
    targetCityName +
    targetAreaName +
    addr01 +
    (addr02 ? `${addr02}巷` : '') +
    (addr03 ? `${addr03}弄` : '') +
    (addr04 && addr05
      ? `${addr04}-${addr05}號`
      : addr04 ? `${addr04}號` : '') +
    (addr06 ? `${addr06}樓` : '') +
    (addr06 && addr07 ? `-${addr07}` : '')
  );
};

/**
 * @description 處理「電話號碼」資料顯示
 * @param telAreaCode 電話區碼
 * @param telNumbers 電話號碼
 * @param telExt 電話分機
 */
const displayTelephone = (telAreaCode?: string, telNumbers?: string, telExt?: string) => {
  if (!telAreaCode || !telNumbers) return '';
  return !telExt ? `${telAreaCode}-${telNumbers}` : `${telAreaCode}-${telNumbers} #${telExt}`;
};

/**
 * @description 處理「國家」資料顯示 (轉中文格式)
 * @param countryId 國家 ID
 */
const displayCountryName = (countryId: string) => {
  const getnationListState = store.getState().common.getnationList;
  const countries = getnationListState.reduce((acc: CountryResp['data'], curr) => acc.concat(curr.data), []);
  const countryName = countries.find((country) => country.countryId === countryId)?.countryName;
  return countryName ?? '';
};

/**
 * @description 處理「旅遊地區（目的地）」資料顯示 (轉中文格式)
 * @param countryId 國家 ID
 */
const displayTravelDestination = (countryId: string) => {
  const getnationListState = store.getState().common.getnationList;
  const countries = getnationListState.reduce((acc: CountryResp['data'], curr) => acc.concat(curr.data), []);
  const countryName = countries.find((country) => country.countryId === countryId)?.countryName;
  return countryId === CountriesIdEnum.TW ? '國內（台、澎、金、馬）' : countryName ? `國外（${countryName}）` : '';
};

/**
 * @description 處理「受益人關係」資料顯示 (轉中文格式)
 * @param ageType 年齡類型
 * @param type 受益人關係選單資料類型
 * @param code 關係代碼
 */
const displayBeneficiaryRelationship = (ageType: TargetAgeTypesEnum, type: string, code: string) => {
  const systemOptionsState = store.getState().common.systemOptions;
  switch (type) {
    case InsuranceAmountAllocateTypesEnum.Precedence: {
      const options = systemOptionsState.filter((param) => param.optSid === (ageType === TargetAgeTypesEnum.AD ? SystemOptionTypesEnum.BfcRelationPrec : SystemOptionTypesEnum.BfcRelationPrecU7));
      const targetOption = options.find((opt) => opt.valueCode === code);
      return targetOption?.keyText ?? '';
    }
    case InsuranceAmountAllocateTypesEnum.Proportion: {
      const options = systemOptionsState.filter((param) => param.optSid === (ageType === TargetAgeTypesEnum.AD ? SystemOptionTypesEnum.BfcRelationProp : SystemOptionTypesEnum.BfcRelationPropU7));
      const targetOption = options.find((opt) => opt.valueCode === code);
      return targetOption?.keyText ?? '';
    }
    default:
      return '';
  }
};

/**
 * @description 處理「受益人關係」資料顯示 (轉中文格式)
 * @param ageType 年齡類型
 * @param type 受益人關係選單資料類型
 * @param code 關係代碼
 */
const displayBirthdayBeneficiaryRelationship = (ageType: TargetAgeTypesEnum, type: string, code: string) => {
  const systemOptionsState = store.getState().common.systemOptions;
  switch (type) {
    case InsuranceAmountAllocateTypesEnum.Precedence: {
      const options = systemOptionsState.filter((param) => param.optSid === (ageType === TargetAgeTypesEnum.AD ? SystemOptionTypesEnum.BfcRelationPrecs : SystemOptionTypesEnum.BfcRelationPrecU7));
      const targetOption = options.find((opt) => opt.valueCode === code);
      return targetOption?.keyText ?? '';
    }
    case InsuranceAmountAllocateTypesEnum.Proportion: {
      const options = systemOptionsState.filter((param) => param.optSid === (ageType === TargetAgeTypesEnum.AD ? SystemOptionTypesEnum.BfcRelationProps : SystemOptionTypesEnum.BfcRelationPropU7));
      const targetOption = options.find((opt) => opt.valueCode === code);
      return targetOption?.keyText ?? '';
    }
    default:
      return '';
  }
};

const displayNewBeneficiaryRelationship = (ageType: TargetAgeTypesEnum, type: string, code: string) => {
  const systemOptionsState = store.getState().common.systemOptions;
  const options = systemOptionsState.filter((param) => param.optSid === (ageType === TargetAgeTypesEnum.AD ? SystemOptionTypesEnum.BfcRelationPrec : SystemOptionTypesEnum.BfcRelationPrecU7));
  const targetOption = options.find((opt) => opt.valueCode === code);
  return targetOption?.keyText ?? '';
};

/**
 * @description 字串隱碼
 * @param str 字串
 * @param startIndex 隱碼開始 index
 * @param endIndex 隱碼結束 index
 */
const protectedStringCharacters = (str: string, startIndex: number, endIndex: number) => {
  if (!str) return str;
  const asterisk = [...Array(endIndex - startIndex)].map(() => '*').join('');
  // return str.substring(0, startIndex) + asterisk + str.substring(endIndex);
  return unicodeSubstring(str, 0, startIndex) + asterisk + unicodeSubstring(str, endIndex, str.length);
};

/**
 * @description 會員密碼隱碼
 * @param passPhrase 密碼
 */
const protectedPassPhrase = (passPhrase: string) => {
  // 隱碼規則：全部隱碼。
  return protectedStringCharacters(passPhrase, 0, passPhrase.length);
};

/**
 * @description 姓名隱碼
 * @param name 姓名
 */
const protectedName = (name: string) => {
  // 隱碼規則：
  // 2 個字：第 2 個字隱碼。
  // 3 個字：第 2 個字隱碼 (只留第 1 個、最後 1 個字明碼)。
  // 4 個字：第 2 ~ 3 個字隱碼 (只留第 1 個、最後 1 個字明碼)。
  // 5 個字：第 2 ~ 4 個字隱碼 (只留第 1 個、最後 1 個字明碼)。
  const strLength = _.toArray(name).length;
  return strLength > 2
    ? protectedStringCharacters(name, 1, strLength - 1)
    : protectedStringCharacters(name, 1, strLength);
};

/**
 * @description 身分證字號隱碼
 * @param id 身分證字號
 */
const protectedPersonalId = (id: string) => {
  // 隱碼規則：第 4 ~ 7 個字隱碼。
  return protectedStringCharacters(id, 4, 7);
};

/**
 * @description 出生年月日隱碼
 * @param birthday 出生年月日
 */
const protectedBirthday = (birthday: string) => {
  // 隱碼規則：100/10/10 => 100/10/**。
  return protectedStringCharacters(birthday, 7, birthday.length);
};

/**
 * @description 電子郵件隱碼
 * @param email 電子郵件
 */
const protectedEmail = (email: string) => {
  if (!email) {
    return '';
  }
  // 隱碼規則：sample@gmail.com => sam***@gmail.com。
  const [id, domain] = email.split('@');
  return `${id[0] ?? ''}${id[1] ?? ''}${id[2] ?? ''}${new Array(id.length <= 3 ? 0 : id.length - 2).join('*')}@${domain}`;
};

/**
 * @description 電話號碼隱碼 (聯絡電話、行動電話)
 * @param phoneNumbers 電話號碼
 */
const protectedPhoneNumbers = (phoneNumbers: string) => {
  // 隱碼規則：第 4 ~ 7 個字隱碼。
  return protectedStringCharacters(phoneNumbers, 4, 7);
};

/**
 * @description 聯絡地址隱碼
 * @param address 聯絡地址
 */
const protectedAddress = (address: string) => {
  // 隱碼規則：第 7 ~ 9 個字隱碼。
  return protectedStringCharacters(address, 6, 9);
};

/**
 * @description 聯絡地址隱碼
 * @param address 聯絡地址
 */
const protectedElementarySchool = (address: string) => {
  // 隱碼規則：第 2 ~
  if (address.length <= 1) {
    address = address + '*';
  }

  return protectedStringCharacters(address, 1, address.length);
};

/**
 * @description 取得系統訊息
 * @param systemMessageId 系統訊息 ID
 */
const fetchSystemMessage = (systemMessageId: SystemMessageIdEnum) => {
  const systemMessagesState = store.getState().common.systemMessages;
  const targetSystemMessage = systemMessagesState.find((message) => message.msgId === systemMessageId);
  return targetSystemMessage;
};

/**
 * @description 金額 (數字) 千分位格式化
 * @param amt 金額 (數字)
 */
const thousandFormat = (amt: string | number) => {
  return amt.toString().replace(/(\d{1,3})(?=(\d{3})+$)/g, '$1,');
};

/**
 * @description 將errorMsg 轉換訊息
 */
const displayErrorMsg = (obj: any): string[] => {
  return Object.entries(obj).filter(item => item.length > 1).reduce<string[]>((pre, item) => {
    if (!item[1]) {
      return [...pre];
    }

    if (Array.isArray(item[1])) {
      return [...pre, ...displayErrorMsg(item[1]).map(subItem => item[0] + '.' + subItem)];
    }

    if (typeof item[1] === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return [...pre, ...displayErrorMsg(item[1])];
    }
    return [...pre, `${item[0] ?? ''}:${item[1] ?? ''}`];
  }, []);
};

/**
 *
 * @param htmlStr 轉換 HTML 字串
 */
const unEscape = (htmlStr: string) => {
  htmlStr = htmlStr.replaceAll(/&lt;/g, '<');
  htmlStr = htmlStr.replaceAll(/&gt;/g, '>');
  htmlStr = htmlStr.replaceAll(/&quot;/g, '"');
  htmlStr = htmlStr.replaceAll(/&#39;/g, '\'');
  htmlStr = htmlStr.replaceAll(/&amp;/g, '&');
  return htmlStr;
};

/**
 * @description Base64 格式轉為 File (Blob)
 * @param base64Str Base64 string
 * @param fileName 檔案名稱
 */
const convertBase64ToFile = (base64Str: string, fileName: string): Promise<File> => new Promise((resolve) => {
  const arr = base64Str.split(',');
  const mimeParse = arr[0].match(/:(.*?);/);
  const mime = mimeParse ? mimeParse[1] : undefined;
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  resolve(new File([u8arr], fileName, { type: mime }));
});

/**
 * @description AES 解密
 * @param encryptedData 已加密的資料
 */
const decryptAES = (encryptedData: string) => {
  const secretKey = 'nSrtRV69xLiCKw2='; // 說明：由後端提供。
  const iv = 'M2OkAS4GHNuF4vY='; // 說明：由後端提供。
  const secretKeyBytes = CryptoJS.enc.Utf8.parse(secretKey);
  const ivBytes = CryptoJS.enc.Utf8.parse(iv);
  const encryptedDataBytes = CryptoJS.enc.Base64.parse(encryptedData);
  const decryptedData = CryptoJS.AES.decrypt({ ciphertext: encryptedDataBytes } as any, secretKeyBytes, { mode: CryptoJS.mode.CBC, iv: ivBytes, padding: CryptoJS.pad.Pkcs7 });
  return decryptedData.toString(CryptoJS.enc.Utf8);
};

/**
 * @description 處理登入者資料 AES 解密
 * @param memberInfo 登入者資料
 */
const decryptMemberInfo = (memberInfo: MbGetinfoResp): MbGetinfoResp => {
  return {
    ...memberInfo,
    USERS: {
      ...memberInfo.USERS,
      ROC_ID: memberInfo.USERS.ROC_ID ? decryptAES(memberInfo.USERS.ROC_ID) : memberInfo.USERS.ROC_ID,
      USER_ID: memberInfo.USERS.USER_ID ? decryptAES(memberInfo.USERS.USER_ID) : memberInfo.USERS.USER_ID,
      MOBILE: memberInfo.USERS.MOBILE ? decryptAES(memberInfo.USERS.MOBILE) : memberInfo.USERS.MOBILE,
      EMAIL: memberInfo.USERS.EMAIL ? decryptAES(memberInfo.USERS.EMAIL) : memberInfo.USERS.EMAIL,
      userName: memberInfo.USERS.userName ? decryptAES(memberInfo.USERS.userName) : memberInfo.USERS.userName
    }
  };
};

/**
 * @description 將 msg 取出超鏈接文字
 */
const handleModifyMessage = (string: string) => {
  const regex = /\{(.+?)\}/g;
  return string.match(regex)?.map(item => item.replace('{', '').replace('}', '')) ?? [];
};

/**
 * @description 根據 {} split msg
 */
const handleSplitMessage = (string: string) => {
  const regex = /\{(.+?)\}/g;
  return string.split(regex);
};

/**
 * @description 同步等待若干毫秒
 * @param milliseconds 毫秒
 */
const delay = (milliseconds: number) => {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
};

/**
 * @description 取得網址檔名
 * @param url 網址
 */
const getFileNameFromUrl = (url: string) => {
  const snippets = url.split('/');
  return snippets[snippets.length - 1];
};

export default {
  appendScriptResource,
  checkDuplicateScript,
  fileSaver,
  generateSessionId,
  handleWindowScrollToTargetSection,
  handleRedirect,
  convertToTWDate,
  convertToADDate,
  moveArrayTargetItem,
  displayTWDate,
  displayAddress,
  displayTelephone,
  displayCountryName,
  displayTravelDestination,
  displayBeneficiaryRelationship,
  displayBirthdayBeneficiaryRelationship,
  protectedStringCharacters,
  protectedPassPhrase,
  protectedName,
  protectedPersonalId,
  protectedBirthday,
  protectedEmail,
  protectedPhoneNumbers,
  protectedAddress,
  fetchSystemMessage,
  thousandFormat,
  displayErrorMsg,
  protectedTWDate,
  protectedElementarySchool,
  unEscape,
  convertBase64ToFile,
  decryptAES,
  decryptMemberInfo,
  handleModifyMessage,
  handleSplitMessage,
  displayNewBeneficiaryRelationship,
  delay,
  getFileNameFromUrl
};
